import { AuthTokenHeader, FetchClient } from '@vp/fetch-client';
import { ILogger } from '@vp/pdp-ubik-logging';

import { ICombinationMedia, ICurrentDocument } from '../../../types/interfaces';
import { buildInstructionsUri } from '../libs/instructionsUrlBuilder';
import { checkWillDocumentOpen } from '../libs/isResourceAvailable';
import { getBestScenePurpose } from '../libs/scenePurposeCalculator';

import { Combination } from './Combination';
import { Image } from './Image';
import { Slide } from './Slide';
import { SlideArray } from './SlideArray';

export class CombinationMedia {
  private constructor(
    private _combination: Combination,
    private _slides: SlideArray
  ) {}

  get thumbnails(): string[] {
    return this._slides.thumbnails;
  }

  hasCompatibleCombination(combination: Combination): boolean {
    return this._combination.isCompatible(combination);
  }

  get combination(): Combination {
    return this._combination;
  }

  get medias(): SlideArray {
    return this._slides;
  }

  get containsVideos(): boolean {
    return this._slides.containsVideos;
  }

  copy(): CombinationMedia {
    return new CombinationMedia(this._combination.copy(), this._slides);
  }

  static from(combinationMedia: ICombinationMedia): CombinationMedia {
    return new CombinationMedia(
      new Combination(combinationMedia.combination),
      SlideArray.from(combinationMedia.medias)
    );
  }

  static fromClasses(combination: Combination, slides: SlideArray): CombinationMedia {
    return new CombinationMedia(combination, slides);
  }

  public async createDocumentSlide(
    tenant: string,
    locale: string,
    productKey: string,
    productVersion: number,
    currentDocument: ICurrentDocument,
    isSceneImprovementsEnabled: boolean,
    fetchClient: FetchClient,
    tokenConfig: AuthTokenHeader,
    logger: ILogger
  ): Promise<boolean> {
    const slide = await this.createSlide(
      tenant,
      locale,
      productKey,
      productVersion,
      currentDocument,
      isSceneImprovementsEnabled,
      fetchClient,
      tokenConfig,
      logger
    );

    if (!slide || !slide.media) {
      return false;
    }

    this._slides.addFirst(slide);

    return true;
  }

  private async createSlide(
    tenant: string,
    locale: string,
    productKey: string,
    productVersion: number,
    currentDocument: ICurrentDocument,
    isSceneImprovementsEnabled: boolean,
    fetchClient: FetchClient,
    tokenConfig: AuthTokenHeader,
    logger: ILogger
  ): Promise<Slide | null> {
    const bestPurposePromise = getBestScenePurpose(
      tenant,
      productKey,
      productVersion,
      this._combination,
      isSceneImprovementsEnabled,
      fetchClient,
      tokenConfig
    );
    const documentUri = this._combination.getDocumentUrlAdaptedToOptions(
      locale,
      productKey,
      productVersion,
      currentDocument.documentUri
    );

    const [bestPurpose, willDocumentOpen] = await Promise.all([
      bestPurposePromise,
      await checkWillDocumentOpen(documentUri),
    ]);
    if (!willDocumentOpen) {
      logger.error('Document not available', {
        documentUri,
      });
      return null;
    }

    const instructionsUri = buildInstructionsUri(documentUri);

    const image = Image.fromDocument(
      tenant,
      locale,
      productKey,
      productVersion,
      currentDocument.altText,
      this._combination.optionSelections,
      instructionsUri,
      bestPurpose
    );

    if (!image) return null;

    return new Slide(image);
  }
}
